import React, { useState, useEffect, useRef } from 'react';
import generalController from '../../../controllers/generalController';
import { convertPostalToCoordinates } from '../../../utils/googleMapHelper';
import { fetchDealerData, fetchModelData } from '../../../controllers/ActionController';
import PreOrder from './PreOrder';
import { localStorageKeys } from '../../../utils/constants';
import { setItemOnLocalStorage } from '../../../utils/helpers';
import { FormExtraParams, Location } from '../../../types';

interface VehicleData {
  id: number;
  isEV: boolean;
  title: string;
  slug: string;
  model: string;
  msrp: number;
  year: string;
  modelYear?: string;
  description: string;
  visual: {
    jelly: string;
    jelly_hover: string;
    jelly_path: string;
    [key: string]: any;
  };
}

interface DealerInfo {
  name: string;
  dealer_code: string;
  [key: string]: any;
}

interface DealerData {
  dealer: DealerInfo;
  userPosition?: Location;
}

interface PreOrderContainerProps {
  formExtraParams: FormExtraParams & {
    modelCode?: string;
    modelOption?: string;
    dealerId?: string;
    source?: string;
    language?: string;
    lastStepBottomContents?: string;
    initStepTopContents?: string;
    introductionText?: string;
    formDisclaimer?: string;
    isPreorderModal?: string;
  };
}

// HOC for "PreOrder" component
const PreOrderContainer: React.FC<PreOrderContainerProps> = (props) => {
  const { modelCode: modelCodeFormParam, modelOption: modelOptionFormParam, dealerId } = props.formExtraParams;
  const [initStep, setInitStep] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [vehicleData, setVehicleData] = useState<VehicleData | null>(null);
  const [dealerData, setDealerData] = useState<DealerData | null>(null);
  const [userLocation, setUserLocation] = useState<Location | null>(null);

  const componentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    handleInitData();
  }, []);

  // Handle initial data with URL params.
  const handleInitData = async () => {
    const params = generalController.getURLSearchParams();

    if (!params) {
      setIsLoading(false);
      return;
    }

    let initVehicleData: VehicleData | false | null = null;
    let initUserPosition: Location | null = null;

    // Vehicle
    if (params.model) {
      const modelParam = decodeURI(params.model).trim();
      const modelParamData = modelParam.split('|');

      if (modelParamData[0] && modelParamData[1]) {
        initVehicleData = await fetchModelData(modelParamData[0], modelParamData[1]);
      }
    } else if (modelCodeFormParam && modelOptionFormParam) {
      initVehicleData = await fetchModelData(modelCodeFormParam, modelOptionFormParam);
    }

    if (initVehicleData) {
      setInitStep(1);
      setVehicleData(initVehicleData);
    } else {
      // If dealerCode is passed but vehicle is not passed,
      // just set the dealer data in localstorage.
      if (params.dealerCode) {
        const dealerData = await fetchDealerData(params.dealerCode);
        if (dealerData && dealerData.dealer) {
          setItemOnLocalStorage(localStorageKeys.DEALER_SELECTED_BY_USER, dealerData.dealer);
        }
      }
      // If Vehicle isn't successfully found, return here.
      setIsLoading(false);
      return;
    }

    // Dealer
    if (params.dealerCode || dealerId) {
      const dealerData = await fetchDealerData(dealerId ? dealerId : params.dealerCode);

      if (dealerData && dealerData.dealer) {
        const { userPosition } = dealerData;

        // Set the dealer data in localstorage
        // as the user's previously selected dealer.
        setItemOnLocalStorage(localStorageKeys.DEALER_SELECTED_BY_USER, dealerData.dealer);

        setDealerData(dealerData as any);
        setInitStep(2);

        // Set dealer geolocation as user location (map center).
        if (userPosition && (userPosition.lat && userPosition.lng)) {
          initUserPosition = userPosition;
        }
      }
    }

    // Location
    if (!initUserPosition) {
      if (params.geolocation) {
        const locationParam = decodeURI(params.geolocation).trim();
        const locationParamDecoded = locationParam.split('|');

        if (locationParamDecoded[0] && locationParamDecoded[1]) {
          initUserPosition = { lat: Number(locationParamDecoded[0]), lng: Number(locationParamDecoded[1]) };
        }
      } else if (params.postalcode) {
        initUserPosition = await convertPostalToCoordinates(params.postalcode);
      }
    }

    if (initUserPosition) {
      setUserLocation(initUserPosition);
    }

    setIsLoading(false);
  };

  return (
    <div className="mz-pre-order-container mz-page-top" ref={componentRef}>
      {
        !isLoading && (
          <PreOrder
            preDefinedVehicleData={vehicleData}
            preDefinedDealerData={dealerData}
            preDefinedUserLocation={userLocation}
            initStep={initStep}
            containerRef={componentRef}
            {...props}
          />
        )
      }
    </div>
  );
};

export default PreOrderContainer;
